import { useRef, useCallback } from 'react';
import api from '../utils/api';

/**
 * Encapsula o ciclo de vida do WebSocket do Chat: conexão, reconexão com backoff
 * exponencial, heartbeat (ping a cada 25s), resync pós-reconexão e flush da fila
 * offline. A tradução evento→estado fica no componente, via:
 *   - onEvento(data, canalId, ws): trata cada mensagem recebida do servidor
 *   - onStatus(conectado: bool): reflete o estado da conexão na UI
 *   - onResync(canalId): busca/mescla mensagens perdidas enquanto esteve offline
 *   - onRecusado({ codigo, motivo, canalId }): recusa PERMANENTE — não haverá reconexão
 *   - pendentesRef: Map client_msg_id -> {canalId, payload} a reenviar na reconexão
 *
 * Os callbacks são lidos via ref para manter `conectar`/`fechar` estáveis
 * (deps []), evitando reconexões espúrias a cada render.
 */

/**
 * Fechamentos que NÃO adianta repetir. Tentar de novo não muda a resposta: a permissão não
 * vai aparecer sozinha, nem o canal vai passar a existir.
 *
 * Antes disto o servidor recusava derrubando o próprio handshake (HTTP 403), o navegador
 * entregava código 1006 sem motivo, e este hook — que não tinha como distinguir de uma queda
 * de rede — reconectava a cada 1s, 2s, 4s… para sempre. Um usuário sem permissão de chat
 * batia no servidor indefinidamente, e a tela só dizia "sem conexão".
 */
const RECUSA_PERMANENTE = {
  4401: 'sessao',      // credencial ausente/expirada/revogada — precisa entrar de novo
  4403: 'permissao',   // autenticado, mas sem acesso a este chat/canal
  4404: 'canal',       // o canal não existe
};

export function useChatSocket({ onEvento, onStatus, onResync, onRecusado, pendentesRef }) {
  const wsRef = useRef(null);
  const reconnectRef = useRef({ tentativas: 0, timer: null, fechadoManual: false });
  const cb = useRef({});
  cb.current = { onEvento, onStatus, onResync, onRecusado, pendentesRef };

  const fechar = useCallback(() => {
    reconnectRef.current.fechadoManual = true;
    if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
  }, []);

  const conectar = useCallback((canalId) => {
    if (!canalId) return;
    reconnectRef.current.fechadoManual = false;

    // Autenticação do WS é via COOKIE HttpOnly (enviado automaticamente no handshake
    // same-origin). NÃO anexamos token na query string — evita vazá-lo em access logs de
    // proxy/APM. O JWT real nunca fica no localStorage (só um placeholder), então o antigo
    // ?token= não autenticava nada de fato.
    const base = (api.defaults.baseURL || '').replace(/^http/, 'ws');
    let url;
    if (base && /^wss?:/.test(base)) {
      url = `${base}/chat/ws/${canalId}`;
    } else {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      url = `${proto}://${window.location.host}/api/chat/ws/${canalId}`;
    }

    try { if (wsRef.current) wsRef.current.close(); } catch {}

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      cb.current.onStatus(true);
      const reconectando = reconnectRef.current.tentativas > 0;
      reconnectRef.current.tentativas = 0;
      // keepalive
      ws._ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ tipo: 'ping' }));
      }, 25000);
      if (reconectando) {
        // 1) puxa o que foi perdido offline; 2) reenvia o pendente (idempotente via client_msg_id).
        cb.current.onResync(canalId);
        cb.current.pendentesRef.current.forEach((v) => {
          if (v.canalId === canalId && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ tipo: 'mensagem', ...v.payload }));
          }
        });
      }
    };

    ws.onmessage = (ev) => {
      let data; try { data = JSON.parse(ev.data); } catch { return; }
      cb.current.onEvento(data, canalId, ws);
    };

    ws.onclose = (ev) => {
      if (ws._ping) clearInterval(ws._ping);
      // Se este socket já foi SUPERADO por uma conexão mais nova (troca/abertura de outro
      // canal fecha o WS anterior na linha acima), ignore o onclose dele: não rebaixa o
      // status nem agenda reconexão para o canal antigo. Sem isto, abrir um canal disparava
      // um "pisca-pisca" de "sem conexão" + reconexão ao canal errado.
      if (wsRef.current !== ws) return;
      cb.current.onStatus(false);
      if (reconnectRef.current.fechadoManual) return;

      // Recusa definitiva: o servidor disse por quê. Insistir seria bater na porta fechada,
      // e a tela precisa dizer o motivo em vez de piscar "sem conexão" para sempre.
      const tipo = RECUSA_PERMANENTE[ev?.code];
      if (tipo) {
        reconnectRef.current.fechadoManual = true;
        wsRef.current = null;
        cb.current.onRecusado?.({ tipo, codigo: ev.code, motivo: ev.reason || '', canalId });
        return;
      }

      // backoff exponencial: 1s,2s,4s,8s... máx 30s
      const t = reconnectRef.current.tentativas++;
      const delay = Math.min(1000 * Math.pow(2, t), 30000);
      reconnectRef.current.timer = setTimeout(() => {
        if (!reconnectRef.current.fechadoManual) conectar(canalId);
      }, delay);
    };

    ws.onerror = () => { try { ws.close(); } catch {} };
  }, []);

  const isOpen = useCallback(() => !!(wsRef.current && wsRef.current.readyState === WebSocket.OPEN), []);

  const sendRaw = useCallback((obj) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }, []);

  return { conectar, fechar, isOpen, sendRaw };
}
